import React from "react";
import { BusinessSearchSelect, type BusinessSearchSelectProps } from "@src/components/business-data-entry";
import { PickerOptionCard } from "@src/shared/ui";
import { formatEmptyDisplayValue } from "@shared/utils/formatEmptyDisplayValue";
import type { StockKanbanContainer } from "../../../../../api/stockKanbanContainers";
import {
    loadStockKanbanSupplyAvailableLotByCode,
    loadStockKanbanSupplyAvailableLotOptions,
    type StockKanbanSupplyAvailableLotOption,
} from "../helpers/loadStockKanbanSupplyLotAvailability";

/** Базовый queryKey селекта доступных партий контейнера КАНБАН. */
export function stockKanbanSupplyAvailableLotQueryKeyBase(container: StockKanbanContainer) {
    return [
        "searchSelect",
        "stockKanbanSupplyAvailableLot",
        container.StockobjCode,
        container.MeasureCode,
        container.DivisionCode,
    ] as const;
}

/** QueryKey byId-резолва обогащённой партии (общий cache с внутренним byId-запросом селекта). */
export function stockKanbanSupplyAvailableLotByIdQueryKey(container: StockKanbanContainer, lotCode: number | null) {
    return [...stockKanbanSupplyAvailableLotQueryKeyBase(container), "byId", lotCode] as const;
}

export type StockKanbanSupplyAvailableLotSearchSelectProps = Omit<
    BusinessSearchSelectProps<StockKanbanSupplyAvailableLotOption, number>,
    "queryKeyBase" | "loadOptions" | "loadById" | "getId" | "getOptionLabel" | "getSelectedLabel"
> & {
    /** Контейнер КАНБАН, для которого выбирается доступная партия. */
    container: StockKanbanContainer;
};

/**
 * Доменный селект партии для комплектования контейнера КАНБАН. \
 * Опции уже обогащены остатками по ячейкам склада-отправителя
 * (см. {@link loadStockKanbanSupplyAvailableLotOptions}), поэтому выбор опции
 * даёт форме все данные для динамического поведения синхронно.
 */
export function StockKanbanSupplyAvailableLotSearchSelect({
    container,
    label = "Партия",
    minSearchLength = 2,
    debounceDelay = 500,
    searchPlaceholder = "Номер партии",
    value,
    onChange,
    optionSlots,
    ...rest
}: StockKanbanSupplyAvailableLotSearchSelectProps) {
    return (
        <BusinessSearchSelect<StockKanbanSupplyAvailableLotOption, number>
            queryKeyBase={stockKanbanSupplyAvailableLotQueryKeyBase(container)}
            loadOptions={(query, signal) => filterOptions(container, query, signal)}
            loadById={(code, signal) => loadStockKanbanSupplyAvailableLotByCode(container, code, { signal })}
            getId={(item) => item.LotCode}
            getOptionLabel={formatOptionLabel}
            getSelectedLabel={formatSelectedLabel}
            value={value}
            onChange={onChange}
            label={label}
            searchPlaceholder={searchPlaceholder}
            minSearchLength={minSearchLength}
            debounceDelay={debounceDelay}
            {...rest}
            optionSlots={{
                ...optionSlots,
                fullscreen:
                    optionSlots?.fullscreen ??
                    ((item) => (
                        <PickerOptionCard
                            title={`Партия ${formatLotDisplayLabel(item)}`}
                            details={[
                                { label: "Остаток", value: item.LotQtyRest },
                                { label: "Остаток в нужной е.д.", value: item.LotQtyRestInNeedMeas },
                                { label: "Ячеек с остатком", value: item.availability.cells?.length ?? 0 },
                            ]}
                        />
                    )),
            }}
        />
    );
}

async function filterOptions(
    container: StockKanbanContainer,
    query: string,
    signal?: AbortSignal,
): Promise<StockKanbanSupplyAvailableLotOption[]> {
    const options = await loadStockKanbanSupplyAvailableLotOptions(container, { signal });
    const normalizedQuery = query.trim().toLocaleLowerCase();

    if (normalizedQuery.length === 0) {
        return options;
    }

    return options.filter((option) => {
        const lotNum = option.Lot?.LotNum.toLocaleLowerCase() ?? "";
        return lotNum.includes(normalizedQuery) || String(option.LotCode).includes(normalizedQuery);
    });
}

function formatOptionLabel(item: StockKanbanSupplyAvailableLotOption): string {
    const lotNum = formatLotDisplayLabel(item);
    return `${lotNum} — остаток ${item.LotQtyRestInNeedMeas}`;
}

function formatSelectedLabel(item: StockKanbanSupplyAvailableLotOption): string {
    return formatLotDisplayLabel(item);
}

function formatLotDisplayLabel(item: StockKanbanSupplyAvailableLotOption): string {
    if (item.Lot == null) {
        return `Код ${item.LotCode}`;
    }

    return formatEmptyDisplayValue(item.Lot.LotNum);
}
